import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { catalogApi, type CategoryNode } from '@/lib/api';
import { useCart } from '@/lib/cart-store';
import { useLocale, useLocalePath } from '@/hooks/useLocalePath';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Price } from './Price';
import type { FormEvent, JSX } from 'react';

/**
 * One root and its children. Opens on hover for a pointer and on focus for a keyboard,
 * which are different events and both have to work — a hover-only menu is unreachable
 * without a mouse, and a click-only menu feels broken to everyone else.
 */
function NavGroup({
  root,
  path,
}: {
  root: CategoryNode;
  path: (to: string) => string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const children = root.children.filter((c) => c.productCount > 0);

  if (children.length === 0)
    return (
      <Link
        to={path(`/kategoria/${root.slug}`)}
        className="whitespace-nowrap px-1.5 py-2 text-[0.8125rem] font-medium transition-colors duration-[var(--dur-tap)] hover:text-[var(--color-brand)] 2xl:px-2 2xl:text-sm"
      >
        {root.name}
      </Link>
    );

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <Link
        to={path(`/kategoria/${root.slug}`)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 whitespace-nowrap px-1.5 py-2 text-[0.8125rem] font-medium transition-colors duration-[var(--dur-tap)] hover:text-[var(--color-brand)] 2xl:px-2 2xl:text-sm"
      >
        {root.name}
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"
             className={`h-3 w-3 transition-transform duration-[var(--dur-tap)] ${open ? 'rotate-180' : ''}`}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 top-full z-50 min-w-56 bg-[var(--color-ground-lift)] p-2 shadow-lg ring-1 ring-[var(--color-line)]"
          >
            <ul>
              {children.map((child) => (
                <li key={child.slug}>
                  <Link
                    to={path(`/kategoria/${child.slug}`)}
                    onClick={() => setOpen(false)}
                    className="flex items-baseline justify-between gap-6 whitespace-nowrap px-3 py-2 text-sm transition-colors duration-[var(--dur-tap)] hover:bg-[var(--color-ground-deep)]"
                  >
                    <span>{child.name}</span>
                    <span className="text-2xs tabular-nums text-[var(--color-muted)]">{child.productCount}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Header(): JSX.Element {
  const { t } = useTranslation();
  const locale = useLocale();
  const path = useLocalePath();
  const navigate = useNavigate();
  const count = useCart((s) => s.items.reduce((sum, i) => sum + i.quantity, 0));
  const openDrawer = useCart((s) => s.openDrawer);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchRef = useRef<HTMLFormElement>(null);

  // Debounced suggestions, so a keystroke does not become a query.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 220);
    return () => clearTimeout(id);
  }, [query]);

  const { data: categories } = useQuery({
    queryKey: ['categories', locale],
    queryFn: () => catalogApi.categories(locale),
    staleTime: 5 * 60_000,
  });

  const { data: suggestions } = useQuery({
    queryKey: ['suggest', debounced, locale],
    queryFn: () => catalogApi.suggest(debounced, locale),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!query.trim()) return;
    navigate(path(`/szukaj?q=${encodeURIComponent(query.trim())}`));
    setQuery('');
  };

  /**
   * Leaves, largest first — the same rule the homepage curates by, so the two agree.
   *
   * They did not. The header took the first six in tree order and the homepage the largest
   * seven, so the nav omitted `Szafki i komody` entirely: 245 products and the biggest
   * category in the catalogue, missing from the only navigation on the page.
   *
   * Leaves rather than roots for the same reason as the homepage: a parent and its own
   * children in one nav offers the same products twice under different names.
   */
  const topLevel = (categories ?? [])
    .flatMap((c) => (c.children.length > 0 ? c.children : [c]))
    .filter((c) => c.productCount > 0)
    .sort((a, b) => b.productCount - a.productCount);

  return (
    <header className="sticky top-0 z-40 bg-[var(--color-ground)]/95 backdrop-blur-sm">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-[var(--color-ink)] focus:px-4 focus:py-2 focus:text-[var(--color-ground)]">
        {t('common.skipToContent')}
      </a>

      <div className="rz-shell flex items-center gap-4 py-4">
        <Link
          to={path('/')}
          className="inline-flex min-h-11 min-w-0 shrink items-center font-display text-base font-semibold tracking-tight sm:shrink-0 sm:text-lg"
        >
          {t('common.brand')}
        </Link>

        {/*
          Roots with a dropdown of their children, rather than six leaf names in a row.
          Ranking leaves by product count put `Szafki i komody`, `Systemy modułowe` and
          `Biurka i stoliki` side by side — three of the longest names in the catalogue,
          filling the bar and leaving the eye nothing to land on. Five roots read as a
          menu; the depth lives one layer down where it belongs.
        */}
        <nav aria-label={t('nav.catalog')} className="ml-6 hidden items-center gap-1 xl:flex">
          {(categories ?? [])
            .filter((root) => root.productCount > 0)
            .map((root) => (
              <NavGroup key={root.slug} root={root} path={path} />
            ))}
          <Link
            to={path('/tkaniny')}
            className="ml-1 whitespace-nowrap px-1.5 py-2 text-[0.8125rem] font-medium text-[var(--color-accent)] transition-colors duration-[var(--dur-tap)] hover:underline underline-offset-4 2xl:px-2 2xl:text-sm"
          >
            {t('nav.fabrics')}
          </Link>
        </nav>

        <form ref={searchRef} onSubmit={submit} role="search" className="relative ml-auto hidden w-48 min-w-28 shrink md:block lg:w-56 2xl:w-64">
          <label htmlFor="site-search" className="sr-only">{t('common.search')}</label>
          <input
            id="site-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('common.searchPlaceholder')}
            className="rz-field py-2 text-xs"
            autoComplete="off"
          />
          <AnimatePresence>
            {suggestions && suggestions.products.length > 0 && query.length >= 2 && (
              <motion.ul
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.16 }}
                className="absolute left-0 right-0 top-full mt-1 bg-[var(--color-ground-lift)] shadow-lg z-50 max-h-80 overflow-auto"
              >
                {suggestions.products.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={path(`/produkt/${s.slug}`)}
                      onClick={() => setQuery('')}
                      className="flex items-center gap-3 p-2 hover:bg-[var(--color-ground-deep)] transition-colors"
                    >
                      {s.thumb && <img src={s.thumb} alt="" width={40} height={40} className="h-10 w-10 bg-[var(--color-ground-deep)] object-contain" />}
                      <span className="flex-1 text-xs leading-tight">{s.name}</span>
                      <Price minor={s.priceGross} className="text-xs font-semibold" />
                    </Link>
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </form>

        <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-2 md:ml-0">
          <LanguageSwitcher compact />
          <Link
            to={path('/konto')}
            className="hidden min-h-11 items-center px-2 text-xs font-semibold hover:text-[var(--color-brand)] sm:inline-flex"
          >
            {t('nav.myAccount')}
          </Link>
          {/* Word on wide screens, icon below `sm`: five word-labelled actions do not fit
              in 360px once each one has a real 44px tap target. */}
          <button
            type="button"
            onClick={openDrawer}
            className="relative inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 px-1 text-xs font-semibold hover:text-[var(--color-brand)] sm:px-2"
            data-testid="cart-button"
            aria-label={count > 0 ? `${t('common.cart')} — ${count}` : t('common.cart')}
          >
            <span className="hidden sm:inline">{t('common.cart')}</span>
            <svg viewBox="0 0 20 20" className="h-5 w-5 sm:hidden" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M3 5h2l1.6 8.4a1 1 0 0 0 1 .8h6.9a1 1 0 0 0 1-.78L17 8H6" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="8.5" cy="16.5" r="1.1" fill="currentColor" stroke="none" />
              <circle cx="14" cy="16.5" r="1.1" fill="currentColor" stroke="none" />
            </svg>
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-brand)] px-1 text-[0.5625rem] font-bold text-[var(--color-ground)] sm:static sm:h-5 sm:min-w-5 sm:px-1.5 sm:text-2xs">
                {count}
              </span>
            )}
          </button>
          {/* Search lives behind this on mobile, where the inline form does not fit. */}
          <button
            type="button"
            className="inline-flex min-h-11 min-w-11 items-center justify-center md:hidden"
            onClick={() => setMobileSearchOpen((v) => !v)}
            aria-expanded={mobileSearchOpen}
            aria-controls="mobile-search"
            aria-label={t('common.search')}
          >
            <svg viewBox="0 0 20 20" className="h-5 w-5" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="9" cy="9" r="6" />
              <path d="m13.5 13.5 4 4" strokeLinecap="round" />
            </svg>
          </button>
          <button
            type="button"
            className="inline-flex min-h-11 min-w-11 items-center justify-center px-1 text-xs font-semibold sm:px-2 xl:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={t('common.menu')}
          >
            <span className="hidden sm:inline">{t('common.menu')}</span>
            <svg viewBox="0 0 20 20" className="h-5 w-5 sm:hidden" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M3 6h14M3 10h14M3 14h14" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* A 755-product catalogue with no search on a phone is not a catalogue. */}
      <AnimatePresence>
        {mobileSearchOpen && (
          <motion.div
            id="mobile-search"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden bg-[var(--color-ground-lift)] md:hidden"
          >
            <form
              role="search"
              className="rz-shell flex gap-2 py-3"
              onSubmit={(event) => {
                submit(event);
                setMobileSearchOpen(false);
              }}
            >
              <label htmlFor="mobile-search-input" className="sr-only">{t('common.search')}</label>
              <input
                id="mobile-search-input"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('common.searchPlaceholder')}
                className="rz-field"
                autoComplete="off"
                autoFocus
              />
              <button type="submit" className="rz-button shrink-0 !px-4" data-testid="mobile-search-submit">
                {t('common.search')}
              </button>
            </form>
            {suggestions && suggestions.products.length > 0 && (
              <ul className="rz-shell max-h-72 overflow-auto pb-3">
                {suggestions.products.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={path(`/produkt/${s.slug}`)}
                      onClick={() => { setQuery(''); setMobileSearchOpen(false); }}
                      className="flex min-h-14 items-center gap-3 py-1"
                    >
                      {s.thumb && <img src={s.thumb} alt="" width={44} height={44} className="h-11 w-11 object-cover" />}
                      <span className="flex-1 text-xs leading-tight">{s.name}</span>
                      <Price minor={s.priceGross} className="text-xs font-semibold" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            id="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden bg-[var(--color-ground-lift)] xl:hidden"
            aria-label={t('nav.catalog')}
          >
            <ul className="rz-shell py-4 space-y-1">
              {topLevel.map((category) => (
                <li key={category.slug}>
                  <Link
                    to={path(`/kategoria/${category.slug}`)}
                    onClick={() => setMenuOpen(false)}
                    className="flex min-h-12 items-center justify-between py-2 text-sm font-medium"
                  >
                    {category.name}
                    <span className="text-2xs text-[var(--color-muted)]">{category.productCount}</span>
                  </Link>
                </li>
              ))}
              <li>
                <Link to={path('/tkaniny')} onClick={() => setMenuOpen(false)} className="flex min-h-12 items-center py-2 text-sm font-medium text-[var(--color-accent)]">
                  {t('nav.fabrics')}
                </Link>
              </li>
              <li className="sm:hidden">
                <Link to={path('/konto')} onClick={() => setMenuOpen(false)} className="flex min-h-12 items-center py-2 text-sm font-medium">
                  {t('nav.myAccount')}
                </Link>
              </li>
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
